import { useEffect, useMemo, useState } from 'react';
import {
  X,
  Search,
  ExternalLink,
  Loader2,
  Send,
  AlertCircle,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { api } from '../lib/api';
import type { Job } from '../lib/store';

interface XeroContact {
  contact_id: string;
  name: string;
  email?: string;
}

interface DryRunResponse {
  dry_run: true;
  line_items: {
    Description: string;
    Quantity: number;
    UnitAmount: number;
    AccountCode: string;
    TaxType?: string;
  }[];
  subtotal: number;
  line_amount_type: string;
  currency_code?: string;
  contact_name?: string;
  reference?: string;
  job_number?: string;
  job_title?: string;
}

export default function PushToXeroModal({
  job,
  onClose,
  onPushed,
}: {
  job: Job;
  onClose: () => void;
  onPushed: (updated: any) => void;
}) {
  const [step, setStep] = useState<'contact' | 'preview'>('contact');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<XeroContact[]>([]);
  const [searching, setSearching] = useState(false);

  // Pre-fill from the client that was chosen when the job was created (if the
  // client is already linked to a Xero contact we can skip the search step).
  const linkedFromClient = !!job.client_xero_customer_id;
  const [contactId, setContactId] = useState<string | null>(job.client_xero_customer_id || null);
  const [contactName, setContactName] = useState<string | null>(
    job.client_xero_customer_id ? job.client_name || null : null
  );
  const [contactEmail, setContactEmail] = useState<string | null>(
    job.client_xero_customer_id ? job.client_email || null : null
  );
  const [autoPreviewed, setAutoPreviewed] = useState(false);
  const [changingContact, setChangingContact] = useState(false);
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dueDate, setDueDate] = useState('');
  const [reference, setReference] = useState(job.job_number || '');
  const [preview, setPreview] = useState<DryRunResponse | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [xeroStatus, setXeroStatus] = useState<{ connected: boolean; tenant_name?: string } | null>(
    null
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/xero/status');
        setXeroStatus(r.data);
      } catch {
        setXeroStatus({ connected: false });
      }
    })();
  }, []);

  // Auto-jump to preview when the client is already linked to a Xero contact
  // — no reason to make the user search again for a contact they've already
  // chosen when creating the job.
  useEffect(() => {
    if (
      xeroStatus?.connected &&
      linkedFromClient &&
      !autoPreviewed &&
      !changingContact &&
      step === 'contact'
    ) {
      setAutoPreviewed(true);
      runPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xeroStatus?.connected, linkedFromClient, changingContact]);

  // Debounced contact search
  useEffect(() => {
    if (!q.trim() || q.trim().length < 2 || !xeroStatus?.connected) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/xero/contacts', { params: { q: q.trim() } });
        setResults(r.data.results || []);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q, xeroStatus?.connected]);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const payload: any = {
        date: date || null,
        due_date: dueDate || null,
        reference: reference || null,
      };
      if (contactId) {
        payload.contact_id = contactId;
      } else if (contactName) {
        payload.contact_name = contactName;
        if (contactEmail) payload.contact_email = contactEmail;
      }
      const res = await api.post(`/jobs/${job.id}/push-to-xero`, payload, {
        params: { dry_run: true },
      });
      setPreview(res.data);
      setStep('preview');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const doPush = async () => {
    setPushing(true);
    try {
      const payload: any = {
        date: date || null,
        due_date: dueDate || null,
        reference: reference || null,
      };
      if (contactId) {
        payload.contact_id = contactId;
      } else if (contactName) {
        payload.contact_name = contactName;
        if (contactEmail) payload.contact_email = contactEmail;
      }
      const res = await api.post(`/jobs/${job.id}/push-to-xero`, payload);
      const data = res.data || {};
      const attached: string[] = data.attached_files || [];
      const attachErrs: string[] = data.attach_errors || [];
      let msg = `Invoice ${data.invoice_number || 'draft'} created in Xero`;
      if (attached.length) {
        msg += ` — ${attached.length} cert${attached.length > 1 ? 's' : ''} attached`;
      }
      toast.success(msg);
      if (attachErrs.length) {
        toast.error(`Some certs failed to attach: ${attachErrs.join('; ')}`);
      }
      onPushed(data);
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Push to Xero failed');
    } finally {
      setPushing(false);
    }
  };

  const canProceed = contactId || contactName;

  const suggestedContactName = useMemo(() => job.client_name || '', [job.client_name]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-pop overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 grid place-items-center">
              <Send className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">Push to Xero</h2>
              <p className="text-xs text-slate-500">
                {job.job_number} · {job.title}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {xeroStatus === null ? (
            <div className="text-center py-10">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
            </div>
          ) : !xeroStatus.connected ? (
            <div className="text-center py-10 space-y-3">
              <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
              <p className="text-slate-700 font-medium">Xero is not connected.</p>
              <p className="text-sm text-slate-500">
                Go to Settings → Xero to connect first.
              </p>
            </div>
          ) : step === 'contact' && linkedFromClient && !changingContact && (previewing || !preview) ? (
            // Auto-previewing because the client is already linked to a Xero contact
            <div className="text-center py-14 space-y-3">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-brand-600" />
              <div>
                <p className="text-slate-700 font-semibold">Preparing invoice for {job.client_name}…</p>
                <p className="text-xs text-slate-500 mt-1">Using the Xero contact linked to this job</p>
              </div>
            </div>
          ) : step === 'contact' ? (
            <div className="space-y-5">
              {/* Xero connection status pill */}
              <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 w-fit">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Connected to <strong>{xeroStatus.tenant_name}</strong>
              </div>

              {/* Contact search */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">
                  Xero contact
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    className="input pl-9"
                    placeholder={`Search Xero (e.g. "${suggestedContactName || 'client name'}")…`}
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setContactId(null);
                      setContactName(null);
                    }}
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                  )}
                </div>

                {results.length > 0 && !contactId && (
                  <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                    {results.map((r) => (
                      <button
                        key={r.contact_id}
                        onClick={() => {
                          setContactId(r.contact_id);
                          setContactName(r.name);
                          setContactEmail(r.email || null);
                          setResults([]);
                          setQ(r.name);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-brand-50 transition"
                      >
                        <div className="font-semibold text-sm text-slate-900">{r.name}</div>
                        {r.email && <div className="text-xs text-slate-500">{r.email}</div>}
                      </button>
                    ))}
                  </div>
                )}

                {contactId && (
                  <div className="mt-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                    <div className="text-sm">
                      <div className="font-semibold text-emerald-800">{contactName}</div>
                      {contactEmail && <div className="text-xs text-emerald-700">{contactEmail}</div>}
                    </div>
                    <button
                      onClick={() => {
                        setContactId(null);
                        setContactName(null);
                        setContactEmail(null);
                        setQ('');
                      }}
                      className="ml-auto text-xs text-emerald-700 hover:text-emerald-900 font-semibold"
                    >
                      Change
                    </button>
                  </div>
                )}

                {!contactId && q && !searching && results.length === 0 && q.trim().length >= 2 && (
                  <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    No match. Xero will create a new contact called "<strong>{q}</strong>".
                    <button
                      onClick={() => {
                        setContactName(q.trim());
                      }}
                      className="ml-auto text-brand-700 hover:text-brand-800 font-semibold"
                    >
                      Use this name →
                    </button>
                  </div>
                )}

                {contactName && !contactId && (
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                    <span className="text-amber-800">
                      New contact "<strong>{contactName}</strong>" will be created in Xero.
                    </span>
                  </div>
                )}
              </div>

              {/* Reference + dates */}
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                    Reference
                  </label>
                  <input
                    className="input"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="Job number"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                    Invoice date
                  </label>
                  <input
                    type="date"
                    className="input"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                    Due date
                  </label>
                  <input
                    type="date"
                    className="input"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 border border-slate-100">
                💡 Invoice lines are computed from this job's <strong>hours to invoice</strong>,{' '}
                <strong>consumables</strong>, and any <strong>parts orders</strong>. Preview to
                confirm before pushing.
              </div>
            </div>
          ) : (
            /* Preview step */
            preview && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <button
                    onClick={() => setStep('contact')}
                    className="text-brand-700 hover:text-brand-800 font-semibold"
                  >
                    ← Back
                  </button>
                  <span>Review before pushing</span>
                </div>

                <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-100">
                    <div>
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Contact
                      </div>
                      <div className="font-semibold text-slate-900">
                        {preview.contact_name || contactName || 'New Xero contact'}
                      </div>
                      {linkedFromClient && contactId === job.client_xero_customer_id && !changingContact && (
                        <div className="text-[11px] text-emerald-700 mt-0.5 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Linked from job
                          <button
                            onClick={() => {
                              setChangingContact(true);
                              setContactId(null);
                              setContactName(null);
                              setContactEmail(null);
                              setQ('');
                              setStep('contact');
                            }}
                            className="ml-1 text-slate-500 hover:text-slate-700 underline"
                          >
                            change
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Reference
                      </div>
                      <div className="text-sm font-mono text-slate-700">
                        {preview.reference || '—'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                      Line items ({preview.line_items.length})
                    </div>
                    <div className="divide-y divide-slate-100">
                      {preview.line_items.map((line, i) => {
                        const lineTotal = line.Quantity * line.UnitAmount;
                        return (
                          <div key={i} className="py-2 flex items-start gap-3 text-sm">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-slate-900">
                                {line.Description}
                              </div>
                              <div className="text-xs text-slate-500">
                                {line.Quantity} × £{line.UnitAmount.toFixed(2)} · Account{' '}
                                {line.AccountCode}
                                {line.TaxType ? ` · ${line.TaxType}` : ''}
                              </div>
                            </div>
                            <div className="font-mono font-semibold text-slate-900 shrink-0">
                              £{lineTotal.toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-sm text-slate-500">
                      Subtotal ({preview.line_amount_type.toLowerCase()} of tax)
                    </span>
                    <span className="text-xl font-bold text-slate-900">
                      {preview.currency_code ? `${preview.currency_code} ` : '£'}
                      {preview.subtotal.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="text-xs text-slate-500 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                  Will be created as a <strong>DRAFT invoice</strong> in Xero. You can review and
                  send it from Xero.
                </div>
                {(() => {
                  const reports: any[] = Array.isArray((job as any).service_reports)
                    ? (job as any).service_reports
                    : (job as any).service_report && (job as any).service_report.kind
                    ? [(job as any).service_report]
                    : [];
                  const boilerCerts = reports.filter((r: any) => r?.kind === 'boiler_service');
                  if (!boilerCerts.length) return null;
                  return (
                    <div className="text-xs text-slate-600 bg-amber-50 border border-amber-100 rounded-lg p-3 flex items-start gap-2">
                      <span className="text-amber-700">📎</span>
                      <span>
                        The boiler service {boilerCerts.length === 1 ? 'certificate' : `${boilerCerts.length} certificates`}{' '}
                        for this job will be automatically attached to the Xero invoice as a
                        branded PDF (visible to the customer via the online invoice link).
                      </span>
                    </div>
                  );
                })()}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        {xeroStatus?.connected && (
          <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
            <button onClick={onClose} className="btn btn-secondary" disabled={previewing || pushing}>
              Cancel
            </button>
            {step === 'contact' ? (
              <button
                onClick={runPreview}
                disabled={!canProceed || previewing}
                className="btn btn-primary"
              >
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                Preview invoice
              </button>
            ) : (
              <button onClick={doPush} disabled={pushing} className="btn btn-primary">
                {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Push to Xero
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
